/**
 * CardSkeleton Component
 *
 * Skeleton loading state for the APCard component using APSkeleton
 */

import { forwardRef } from 'react';

import type { ICardSkeletonProps } from '../types';

import { APSkeleton } from '../../../atoms/skeleton';

interface ICardSkeletonExtendedProps extends ICardSkeletonProps {
  className?: string;
  'data-testid'?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

export const CardSkeleton = forwardRef<HTMLDivElement, ICardSkeletonExtendedProps>(
  (
    {
      lines = 3,
      showHeader = true,
      showActions = true,
      className = '',
      'data-testid': testId,
      ...rest
    },
    ref
  ) => {
    const skeletonClasses = ['ap-card', 'ap-card--skeleton', className].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={skeletonClasses} data-testid={testId} {...rest}>
        {/* Skeleton Header */}
        {showHeader && (
          <div className="ap-card__header">
            <APSkeleton variant="text" width="60%" height="16px" />
          </div>
        )}

        {/* Skeleton Content */}
        <div className="ap-card__content">
          {Array.from({ length: lines }, (_, index) => (
            <APSkeleton
              key={index}
              variant="text"
              width={index === 0 ? '80%' : index === lines - 1 ? '60%' : '90%'}
              height={index === 0 ? '24px' : '14px'}
              style={{
                marginBottom: index === 0 ? '8px' : index === lines - 1 ? '0' : '4px',
              }}
            />
          ))}
        </div>

        {/* Skeleton Actions */}
        {showActions && (
          <div className="ap-card__actions">
            <APSkeleton variant="rounded" width="80px" height="32px" />
            <APSkeleton variant="rounded" width="80px" height="32px" />
          </div>
        )}
      </div>
    );
  }
);

CardSkeleton.displayName = 'CardSkeleton';

export default CardSkeleton;
